import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Network } from '@capacitor/network';
import { Capacitor } from '@capacitor/core';

export interface ConnectionState {
  isConnected: boolean;
  isStable: boolean;
  lastCheck: Date;
  retryCount: number;
}

@Injectable({
  providedIn: 'root'
})
export class ConnectionStateService {
  private connectionStateSubject = new BehaviorSubject<ConnectionState>({
    isConnected: true,
    isStable: true,
    lastCheck: new Date(),
    retryCount: 0
  });

  private isCheckingConnection = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private connectionCheckInterval: any;
  private readonly STABLE_CONNECTION_CHECKS = 2; // Reduzido de 3 para 2
  private readonly CONNECTION_CHECK_INTERVAL = 3000; // Reduzido de 5 para 3 segundos
  private readonly RETRY_DELAY = 1000; // Reduzido de 2 para 1 segundo

  constructor() {
    this.initializeConnectionMonitoring();
  }

  public getConnectionState(): Observable<ConnectionState> {
    return this.connectionStateSubject.asObservable();
  }

  public getCurrentConnectionState(): ConnectionState {
    return this.connectionStateSubject.value;
  }

  private async initializeConnectionMonitoring() {
    if (Capacitor.isNativePlatform()) {
      // Monitora mudanças de estado da rede
      Network.addListener('networkStatusChange', (status) => {
        this.updateConnectionState(status.connected, false);
      });

      // Verificação inicial
      const status = await Network.getStatus();
      this.updateConnectionState(status.connected, false);
    }
  }

  private updateConnectionState(isConnected: boolean, isStable: boolean) {
    const currentState = this.connectionStateSubject.value;
    
    // Evita atualizações desnecessárias que podem causar recursão
    if (currentState.isConnected === isConnected && currentState.isStable === isStable) {
      return;
    }
    
    const newState: ConnectionState = {
      isConnected,
      isStable,
      lastCheck: new Date(),
      retryCount: isConnected ? 0 : currentState.retryCount + 1
    };

    this.connectionStateSubject.next(newState);
    console.log('Connection state updated:', newState);
  }

  public async checkConnectionStability(): Promise<boolean> {
    if (this.isCheckingConnection) {
      return this.connectionStateSubject.value.isStable;
    }

    this.isCheckingConnection = true;
    console.log('Checking connection stability...');

    try {
      if (!Capacitor.isNativePlatform()) {
        // Para web, assume que está conectado
        this.updateConnectionState(true, true);
        return true;
      }

      let stableChecks = 0;
      const maxChecks = this.STABLE_CONNECTION_CHECKS;

      for (let i = 0; i < maxChecks; i++) {
        const status = await Network.getStatus();
        
        if (status.connected) {
          stableChecks++;
          console.log(`Connection check ${i + 1}/${maxChecks}: Connected`);
        } else {
          console.log(`Connection check ${i + 1}/${maxChecks}: Not connected`);
          stableChecks = 0; // Reset counter if disconnected
        }

        // Aguarda um pouco entre as verificações (reduzido para 500ms)
        if (i < maxChecks - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      const isStable = stableChecks === maxChecks;
      this.updateConnectionState(stableChecks > 0, isStable);
      
      console.log(`Connection stability check completed. Stable: ${isStable}`);
      return isStable;

    } catch (error) {
      console.error('Error checking connection stability:', error);
      this.updateConnectionState(false, false);
      return false;
    } finally {
      this.isCheckingConnection = false;
    }
  }

  public async waitForStableConnection(maxWaitTime: number = 3000): Promise<boolean> {
    const startTime = Date.now();
    
    while (Date.now() - startTime < maxWaitTime) {
      const isStable = await this.checkConnectionStability();
      if (isStable) {
        return true;
      }
      
      // Aguarda antes da próxima verificação
      await new Promise(resolve => setTimeout(resolve, this.RETRY_DELAY));
    }
    
    return false;
  }

  // Método mais rápido para operações que precisam ser responsivas (como filtros)
  public async quickConnectionCheck(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      return true; // Para web, assume que está conectado
    }

    try {
      const { Network } = await import('@capacitor/network');
      const status = await Network.getStatus();
      this.updateConnectionState(status.connected, status.connected);
      return status.connected;
    } catch (error) {
      console.error('Error in quick connection check:', error);
      return false;
    }
  }

  public shouldShowConnectionError(): boolean {
    const state = this.connectionStateSubject.value;
    const now = new Date();
    const timeSinceLastCheck = now.getTime() - state.lastCheck.getTime();
    
    // Só mostra erro se:
    // 1. Não está conectado
    // 2. Já passou tempo suficiente desde a última verificação (evita spam)
    // 3. Não está no meio de uma verificação
    return !state.isConnected && 
           timeSinceLastCheck > 30000 && // 30 segundos
           !this.isCheckingConnection;
  }

  public resetConnectionState() {
    // Evita recursão infinita verificando se já está resetando
    if (this.isCheckingConnection) {
      return;
    }
    this.updateConnectionState(false, false);
  }

  public startPeriodicConnectionCheck() {
    if (this.connectionCheckInterval) {
      clearInterval(this.connectionCheckInterval);
    }

    this.connectionCheckInterval = setInterval(async () => {
      if (Capacitor.isNativePlatform()) {
        const status = await Network.getStatus();
        this.updateConnectionState(status.connected, false);
      }
    }, this.CONNECTION_CHECK_INTERVAL);
  }

  public stopPeriodicConnectionCheck() {
    if (this.connectionCheckInterval) {
      clearInterval(this.connectionCheckInterval);
      this.connectionCheckInterval = null;
    }
  }
}
